/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    ContentInfoCardKernel,
    InfoCardKernel,
    InfoCardKernelType,
    OfferInfoCardKernel,
    RecordingInfoCardKernel,
    UnifiedCollectionInfoCardKernel,
} from 'client-code/actions';
import { EpisodeInfo } from 'client-code/episode-guide';
import { UpcomingOffer } from 'client-code/upcoming-offers';

import { CollectionFilters } from '../../store/collectionscreen/CollectionFilters';
import { getFilterType } from '../../store/collectionscreen/collectionscreen.reducers';
import {
    IInfoCard,
    IInfoCardProperty,
    InfoCardManager,
    InfoCardUserInputHandler,
} from '../infocard';
import { NdvrInteractor } from '../interactors';
import { SharedPrefConstants, SharedPreferences } from '../storage';
import { TEpisodeParams } from '../types';

export class CollectionInfoCardUserInputHandler extends InfoCardUserInputHandler {
    private mInfoCard: IInfoCard = InfoCardManager.getInstance();

    private async horizontalKeyNavigationHelper(direction: 'left' | 'right'): Promise<void> {
        const {
            activeItemIndex,
            seasonInfo,
            initialPageList,
            initialPageStartIndex,
            totalItemCount,
            ucid,
            partnerId,
            contentSupplierPartnerId,
            collapsedVodItemId,
            filter,
            sortOrder,
        } = this.mInfoCard.getContextData(CollectionInfoCardUserInputHandler.name) || {};

        let tActiveIdx: number = activeItemIndex + (direction === 'left' ? -1 : 1);
        let tActiveSeasonIndex = seasonInfo?.activeSeasonIndex ?? -1;

        if (
            direction === 'left' &&
            ![-1, 0].includes(tActiveSeasonIndex) &&
            tActiveIdx === seasonInfo.seasonRanges[tActiveSeasonIndex]
        ) {
            tActiveIdx -= 1;
            tActiveSeasonIndex -= 1;
        } else if (
            direction === 'right' &&
            ![-1, (seasonInfo?.seasonRanges?.length || 0) - 1].includes(tActiveSeasonIndex) &&
            tActiveIdx === seasonInfo.seasonRanges[tActiveSeasonIndex + 1]
        ) {
            tActiveIdx += 1;
            tActiveSeasonIndex += 1;
        }

        if (tActiveIdx < 0 || tActiveIdx >= totalItemCount) return;

        let selectedItem;
        const isItemInInitialPage =
            tActiveIdx >= initialPageStartIndex &&
            tActiveIdx <= initialPageStartIndex + initialPageList.length - 1;
        if (isItemInInitialPage) selectedItem = initialPageList[tActiveIdx - initialPageStartIndex];
        else if (filter === CollectionFilters.UPCOMING_DETAIL) {
            const collectionInfoSharedPref = new SharedPreferences(
                SharedPrefConstants.COLLECTION_INFO_PREF_KEY,
            );
            const upcomingInfo = collectionInfoSharedPref.getJson(
                SharedPrefConstants.COLLECTION_UPCOMING_FILTER_INFO,
            ) as unknown as { minEndTime: string; requestToken: string };
            const response = await new NdvrInteractor().getUpcomingOffersForCollectionId({
                objectId: ucid,
                minEndTime: upcomingInfo.minEndTime,
                offset: tActiveIdx,
                limit: 1,
                screenType: 'infocard',
                requestToken: upcomingInfo.requestToken,
            });
            [selectedItem] = response.upcomingOffers;
        } else {
            const response = await new NdvrInteractor().getEpisodes({
                unifiedCollectionId: ucid,
                limit: 1,
                offset: tActiveIdx - (tActiveSeasonIndex + 1),
                filter: getFilterType(filter),
                sort: sortOrder,
                partnerId,
                contentSupplierPartnerId,
                collapsedVodItemId,
            } as TEpisodeParams);
            [selectedItem] = response.episodeInfo;
        }

        this.mInfoCard.hideActionButtons();
        this.mInfoCard.setInfoCardKernel(
            this.getInfoCardKernel(selectedItem, filter, {
                partnerId,
                collapsedVodItemId,
                contentSupplierPartnerId,
            }),
        );
        this.mInfoCard.setContextData(CollectionInfoCardUserInputHandler.name, {
            activeItemIndex: tActiveIdx,
            seasonInfo:
                tActiveSeasonIndex !== -1
                    ? { ...seasonInfo, activeSeasonIndex: tActiveSeasonIndex }
                    : seasonInfo,
            initialPageList,
            initialPageStartIndex,
            totalItemCount,
            ucid,
            filter,
            sortOrder,
        });
        this.mInfoCard.setProperties({
            // if first item is season strip, then starting index will be 1
            hasItemsBefore: tActiveIdx > (seasonInfo?.seasonRanges ? 1 : 0),
            hasItemsAfter: tActiveIdx < totalItemCount - 1,
        });
    }

    /**
     * @override
     */
    public onKeyLeft(properties: IInfoCardProperty): void {
        if (!properties.hasItemsBefore) return;
        this.horizontalKeyNavigationHelper('left');
    }

    /**
     * @override
     */
    public onKeyRight(properties: IInfoCardProperty): void {
        if (!properties.hasItemsAfter) return;
        this.horizontalKeyNavigationHelper('right');
    }

    // eslint-disable-next-line class-methods-use-this
    public getInfoCardKernel(
        item: EpisodeInfo | UpcomingOffer | string,
        filter: CollectionFilters,
        vodContext?: {
            partnerId?: string;
            contentSupplierPartnerId?: string;
            collapsedVodItemId?: string;
        },
    ): InfoCardKernel {
        if (filter === CollectionFilters.NONE)
            return {
                infoCardKernelType: InfoCardKernelType.UnifiedCollectionInfoCardKernel,
                unifiedCollectionId: item as string,
                partnerId: vodContext?.partnerId,
                contentSupplierPartnerId: vodContext?.contentSupplierPartnerId,
                collapsedVodItemId: vodContext?.collapsedVodItemId,
            } as UnifiedCollectionInfoCardKernel;

        if (filter === CollectionFilters.UPCOMING_DETAIL)
            return {
                infoCardKernelType: InfoCardKernelType.OfferInfoCardKernel,
                offerId: (item as UpcomingOffer).offerId,
            } as OfferInfoCardKernel;

        if ((item as EpisodeInfo).recordingId)
            return {
                infoCardKernelType: InfoCardKernelType.RecordingInfoCardKernel,
                recordingId: (item as EpisodeInfo).recordingId,
            } as RecordingInfoCardKernel;

        return {
            infoCardKernelType: InfoCardKernelType.ContentInfoCardKernel,
            contentId: (item as EpisodeInfo).contentId,
            partnerId: vodContext?.partnerId,
            contentSupplierPartnerId: vodContext?.contentSupplierPartnerId,
            collapsedVodItemId: vodContext?.collapsedVodItemId,
        } as ContentInfoCardKernel;
    }
}
